import React, { useState, useEffect, useRef } from "react";
import ReactMapGL, {Source, Layer, Popup, LinearInterpolator, WebMercatorViewport, NavigationControl } from 'react-map-gl';
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';
import InfoBox from './infobox';
import ListBox from './listbox';
import center from '@turf/center';
import bbox from '@turf/bbox';
import bboxPolygon from '@turf/bbox-polygon';
import {featureCollection} from "@turf/helpers";
import * as d3 from 'd3';
import _ from 'lodash';

import * as mapstyle from './mapstyle.json';

function GLMap(props) {
  const idField = props.idField || "id";

  const [viewport, setViewport] = useState({
    width: props.mapWidth || '100%',
    height: props.mapHeight || 667,
    latitude: props.latitude || 30.97,
    longitude: props.longitude || -80.42,
    zoom: props.zoom || 4.25
    /*
    pitch: 60.5,
    bearing: -47.3985
    */
  });

  const [styles, setStyles] = useState({});
  const [layerData, setLayerData] = useState({});
  const [catalog, setCatalog] = useState([]);
  const [legend, setLegend] = useState(null);
  const [allBBox, setAllBBox] = useState(null);
  const [filteredCatalog, setFilteredCatalog] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [infoBoxDetails, setInfoBoxDetails] = useState(null);
  const [hoveredIds, setHoverIds] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filteredIds, setFilteredIds] = useState([]);
  const [popup, setPopup] = useState("");
  const [lastViewport, setLastViewport] = useState(viewport);

  useEffect(() => {
    if (!props.layerSources) {
      return;
    }

    // @TODO >1 sources
    // good pattern: https://github.com/facebook/react/issues/14326#issuecomment-472043812
    let {url, transform} = props.layerSources;
    d3.json(url).then(d => {
      Promise.resolve(transform(d)).then((transformed) => {
        // build sources from returned data and styles
        // extract and merge individual features
        let data = {},
          allFeatures = {
            type: "FeatureCollection",
            features: [],
          };

        transformed.catalog.forEach((ce) => {
          Object.entries(ce.data).forEach(([layerName, feature]) => {
            if (!data.hasOwnProperty(layerName)) {
              data[layerName] = {
                type: "FeatureCollection",
                features: [],
              };
            }

            data[layerName].features.push(feature);
            allFeatures.features.push(feature);
          });
        });

        // pad out missing styles from normal
        // @TODO: ensure normal defined
        let newStyles = {};
        Object.entries(transformed.styles).forEach(([layerName, styleObj]) => {
          newStyles[layerName] = {
            normal: styleObj.normal,
            hovered: styleObj.hovered || styleObj.normal,
            selected: styleObj.selected || styleObj.normal,
            filtered: styleObj.filtered || styleObj.normal,
            otherHovered: styleObj.otherHovered || styleObj.normal,
            otherSelected: styleObj.otherSelected || styleObj.normal,
            otherFiltered: styleObj.otherFiltered || styleObj.normal,
          };
        });

        setStyles(newStyles);
        setLayerData(data);
        setCatalog(transformed.catalog);
        if (transformed.legend) {
          setLegend(transformed.legend);
        }
        setAllBBox(bbox(allFeatures));
      });
    });
  }, [props.layerSources]);

  // update filtered catalog when filtertext OR catalog changes
  useEffect(() => {
    if (!catalog) {
      return;
    }

    if (!filterText) {
      setFilteredCatalog(Array.from(catalog));
      setFilteredIds([]);
      return;
    }

    let filteredCatalog = catalog.filter(v => {
      if (_.isArray(v.title)) {
        return _.some(v.title.map(vv => vv.value.toString().toLowerCase().indexOf(filterText.toLowerCase()) !== -1));
      } else if (_.isObject(v.title)) {
        // it's likely a react fragment, we don't have a great way to test for this
        // pull out any visible text
        function getVisibleText(node) {
          if (_.isString(node)) {
            return node;
          }

          // console.info("NODE", node, "HAS", _.has(node, 'props.children'));

          if (_.has(node, 'props.children')) {
            if (_.isString(node.props.children)) {
              return node.props.children;
            } else if (_.isArray(node.props.children)) {
              return node.props.children.map(n => {
                return getVisibleText(n);
              })
            }
          }

          return null;
        }

        const allem = getVisibleText(v.title).filter(t => !!t)
        return _.some(allem.map(vv => vv.toString().toLowerCase().indexOf(filterText.toLowerCase()) !== -1));
      }
      else {
        // Vdebugger;
        // console.info(ReactDOMServer.renderToString(v.title));
        return v.title.toString().toLowerCase().indexOf(filterText.toLowerCase()) !== -1;
      }
    });

    let newIds = filteredCatalog.map(ce => ce.key); // @TODO is KEY always right here?

    setFilteredCatalog(filteredCatalog);
    setFilteredIds(newIds);
  }, [filterText, catalog]);

  // zoom into filtered elements after a timeout period
  useEffect(() => {
    const timerId = setTimeout(() => {
      let filteredBox = undefined;
      if (filteredIds.length) {
        filteredBox = bbox(
            featureCollection(filteredCatalog.map(ce => bboxPolygon(ce.boundingBox)))
          );
      }

      zoomToExtents(filteredBox);
    }, 500);

    return () => {
      clearTimeout(timerId);
    }
  }, [filteredIds])

  // auto select catalog entry via url queryparam
  // this is specific to FACT but too difficult to make generic, and GLMap needs to be backported from DaViT anyway
  useEffect(() => {
    if (!catalog.length) {
      return;
    }

    const u = new URLSearchParams(window.location.search),
      project = u.get('project');

    if (!project) {
      return;
    }

    // find in catalog
    const catEntries = catalog.filter(ce => ce.key === project),
      catEntry = catEntries.length === 1 ? catEntries[0] : undefined;

    if (catEntry) {
      onListItemClick(catEntry, {preventDefault: () => {}})
    }
  }, [catalog])

  /**
   * Calculates a pixel number from the value if the value looks like a percentage (0 - 1).
   * Uses viewportProp ('width' or 'height') to pull correct value from viewport.
   * 
   * @param {Number} val 
   * @param {String} viewportProp 
   */
  const _calcPadding = (val, viewportProp) => {
    if (val < 1 && !Number.isInteger(val)) {
      return val * viewport[viewportProp];
    }
    return val;
  }

  const _viewportForExtents = (minLon, minLat, maxLon, maxLat, padding = 40) => {
    // fixup padding object if any member contains a percentage
    if (typeof padding === "object") {
      padding = {
        left:   _calcPadding(padding.left   || 40, 'width'),
        right:  _calcPadding(padding.right  || 40, 'width'),
        top:    _calcPadding(padding.top    || 40, 'height'),
        bottom: _calcPadding(padding.bottom || 40, 'height'),
      };
    }

    let newViewport = new WebMercatorViewport(viewport),
      {longitude, latitude, zoom} = newViewport.fitBounds([
        [minLon, minLat],
        [maxLon, maxLat]
      ], { padding: padding });

    return {
      latitude: latitude,
      longitude: longitude,
      zoom: zoom
    }
  }

  const zoomToExtents = (extents, padding=props.zoomToPadding || {top: 40, left: 400, right: 40, bottom: 40}) => {
    if (!extents) {
      extents = allBBox;
    }
    if (!extents) {
      return;
    }

    let [minLon, minLat, maxLon, maxLat] = extents,
      {latitude, longitude, zoom} = _viewportForExtents(minLon, minLat, maxLon, maxLat, padding);

    setViewport({
      ...viewport,
      latitude: latitude,
      longitude: longitude,
      zoom: Math.min(zoom, props.maxZoom || 10),
      transitionInterpolator: new LinearInterpolator(),
      transitionDuration: 500
    })
  }

  const clearSelection = (zoomToPrevious = true) => {
    setSelectedIds([]);
    setInfoBoxDetails(null)
    if (zoomToPrevious) {
      setViewport({
        ...lastViewport,
        transitionInterpolator: new LinearInterpolator(),
        transitionDuration: 500
      });
    }
  }

  const _renderInfoBox = () => {
    return infoBoxDetails && (
      <InfoBox
        {...infoBoxDetails}
        onClose={clearSelection}
      >
      </InfoBox>
    )
  }

  const onListItemClick = (value, e) => {
    setInfoBoxDetails(value);
    setLastViewport(viewport);
    zoomToExtents(value.boundingBox);
    setSelectedIds([value.key]);
    e.preventDefault();
  }

  const onListItemMouseOver = (value, e) => {
    setPopup(value.popup);
    setHoverIds([value.key]);
  }

  const onListItemMouseOut = (value, e) => {
    setPopup("");
    setHoverIds([]);
  }

  const onMapHover = pe => {
    if (pe.features && pe.features.length) {
      // @TODO: we support multiple popups here, but they don't look very good unless their position has
      // been computed to not overlap.  Currently disabled, will use first feature it finds. Remove .slice() 
      // to see results.
      let newIds = pe.features.slice(0, 1).map(f => f.properties[idField]),
        catalogEntries = catalog.filter(f => newIds.indexOf(f.key) !== -1),
        combinedPopup = (<>
          {catalogEntries.map(ce => {
            return <React.Fragment key={ce.key}>
              {ce.popup}
            </React.Fragment>
          })}
        </>)

      if (popup !== combinedPopup) {
        setPopup(combinedPopup);
        setHoverIds(newIds);
      }
    } else {
      if (popup !== "") {
        setPopup("")
        setHoverIds([]);
      }
    }
  };

  const onMapClick = pe => {
    if (pe.features.length) {
      // find corresponding id in catalog
      // @TODO: we could support multiples here
      let newIds = pe.features.slice(0, 1).map(f => f.properties[idField]),
        catalogEntries = catalog.filter(f => newIds.indexOf(f.key) !== -1);

      setInfoBoxDetails(catalogEntries[0]);
      setLastViewport(viewport);
      zoomToExtents(catalogEntries[0].boundingBox);
      setSelectedIds(newIds);

    } else {
      clearSelection(false);
    }
  };

  /**
   * The filter textbox is typically displayed in the Listbox/Catalog,
   * but acted upon at this level. This handler should be passed into the
   * listbox and acted upon when that filter changes.
   * 
   * @param {string|Object} value 
   */
  const onFilterChanged = (value) => {
    setFilterText(value);
  }

  const onAutoResize = (e) => {
    setViewport({
      ...viewport,
      ...e
    });
  }

  return (
    <div className="glmap-container">
      <div className="glmap-internal-container">
        <AutoSizer disableHeight onResize={onAutoResize}>
          {({ asWidth }) => (
            <ReactMapGL
              {...viewport}
              mapStyle={props.mapStyle || mapstyle}
              onClick={onMapClick}
              interactiveLayerIds={
                props.interactiveLayerIds || Object.keys(layerData)
              }
              onHover={onMapHover}
              onViewportChange={setViewport}
              mapboxApiAccessToken={process.env.MAPBOX_TOKEN}
            >
              <div className="nav-holder">
                <NavigationControl />
              </div>

              {Object.entries(layerData).map(([layerName, fc]) => {
                return (
                  <Source
                    id={layerName}
                    type="geojson"
                    data={fc}
                    key={layerName}
                  >
                    {/*
                     * The invisible layer used for interactiveLayers in mapbox.  Always present, never visible.
                     */}
                    <Layer id={layerName} {...styles[layerName].normal} />

                    {/*
                     * "Normal" - on when nothing hovered or selected.
                     */}
                    <Layer
                      id={layerName + "normal"}
                      {...styles[layerName].normal}
                      filter={[
                        "all",
                        ["==", ["length", ["literal", hoveredIds]], 0],
                        ["==", ["length", ["literal", selectedIds]], 0],
                      ]}
                    />

                    {/*
                     * "otherHovered" - styling for all features that don't match the hovered item(s).
                     * Will not show if anything selected.
                     * if you don't specify this, the Normal style is copied.
                     */}
                    <Layer
                      id={layerName + "otherHovered"}
                      {...styles[layerName].otherHovered}
                      filter={[
                        "all",
                        [">", ["length", ["literal", hoveredIds]], 0],
                        [
                          "!",
                          ["in", ["get", idField], ["literal", hoveredIds]],
                        ],
                        ["==", ["length", ["literal", selectedIds]], 0],
                      ]}
                    />

                    {/*
                     * "Hovered" - styling for the feature (future: potentially multiple) currently hovered.
                     * if you don't specify this, the Normal style is copied.
                     */}
                    <Layer
                      id={layerName + "hovered"}
                      {...styles[layerName].hovered}
                      filter={["in", ["get", idField], ["literal", hoveredIds]]}
                    />

                    {/*
                     * "otherFiltered" - styling for all features that don't match the Filtered item(s).
                     * Will not show if anything selected. Only shown if some filter text is entered.
                     * if you don't specify this, the Normal style is copied.
                     */}
                    <Layer
                      id={layerName + "otherFiltered"}
                      {...styles[layerName].otherFiltered}
                      filter={[
                        "all",
                        [">", ["length", ["literal", filterText]], 0],
                        [
                          "!",
                          ["in", ["get", idField], ["literal", filteredIds]],
                        ],
                        ["==", ["length", ["literal", selectedIds]], 0],
                      ]}
                    />

                    {/*
                     * "Filtered" - styling for the feature (future: potentially multiple) currently Filtered.
                     * if you don't specify this, the Normal style is copied.
                     */}
                    <Layer
                      id={layerName + "Filtered"}
                      {...styles[layerName].filtered}
                      filter={[
                        "in",
                        ["get", idField],
                        ["literal", filteredIds],
                      ]}
                    />

                    {/*
                     * "otherSelected" - styling for all features that don't match the selected item(s).
                     * if you don't specify this, the Normal style is copied.
                     */}
                    <Layer
                      id={layerName + "otherSelected"}
                      {...styles[layerName].otherSelected}
                      filter={[
                        "all",
                        [">", ["length", ["literal", selectedIds]], 0],
                        [
                          "!=",
                          ["get", idField],
                          infoBoxDetails ? infoBoxDetails[idField] : "",
                        ],
                      ]}
                    />

                    {/*
                     * "Selected" - styling for the feature (future: potentially multiple) currently selected.
                     * if you don't specify this, the Normal style is copied.
                     */}
                    <Layer
                      id={layerName + "selected"}
                      {...styles[layerName].selected}
                      filter={[
                        "all",
                        [">", ["length", ["literal", selectedIds]], 0],
                        [
                          "==",
                          ["get", idField],
                          infoBoxDetails ? infoBoxDetails[idField] : "",
                        ],
                      ]}
                    />
                  </Source>
                );
              })}

              {popup}
              {infoBoxDetails && infoBoxDetails.popup}
            </ReactMapGL>
          )}
        </AutoSizer>

        <div className="overlay-container">
          {!props.catalogTable && (
            <ListBox
              title={props.catalogTitle}
              catalog={filteredCatalog}
              catalogTable={props.catalogTable || false}
              catalogTableColumns={props.catalogTableColumns || []}
              catalogTableHeight={props.catalogTableHeight || null}
              idField={idField}
              selected={infoBoxDetails}
              selectedIds={selectedIds}
              hovered={hoveredIds}
              onListItemClick={onListItemClick}
              onListItemMouseOver={onListItemMouseOver}
              onListItemMouseOut={onListItemMouseOut}
              onFilterChanged={onFilterChanged}
              legend={legend}
              filterText={filterText}
              showFilter={props.showFilter || false}
            ></ListBox>
          )}

          {_renderInfoBox()}

          {props.viewportDebug && (
            <div className="viewport-debug">
              <div className="panel">
                <div className="panel-body">
                  <dl>
                    {Object.entries(viewport).reduce((acc, v, idx) => {
                      if (
                        typeof v[1] !== "number" ||
                        v[0].startsWith("max") ||
                        v[0].startsWith("min") ||
                        v[0].startsWith("transition")
                      ) {
                        return acc;
                      }

                      return acc.concat([
                        <dt key={`viewp${idx}`}>{v[0]}</dt>,
                        <dd key={`viewp2${idx}`}>{v[1]}</dd>,
                      ]);
                    }, [])}
                  </dl>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {props.catalogTable && (
        <ListBox
          title={props.catalogTitle}
          catalog={filteredCatalog}
          catalogTable={props.catalogTable || false}
          catalogTableColumns={props.catalogTableColumns || []}
          catalogTableClasses={props.catalogTableClasses || []}
          catalogTableHeight={props.catalogTableHeight || null}
          idField={idField}
          selected={infoBoxDetails}
          selectedIds={selectedIds}
          hovered={hoveredIds}
          onListItemClick={onListItemClick}
          onListItemMouseOver={onListItemMouseOver}
          onListItemMouseOut={onListItemMouseOut}
          onFilterChanged={onFilterChanged}
          filterText={filterText}
        ></ListBox>
      )}
    </div>
  );
}

export default GLMap;